import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useState, type CSSProperties, type PointerEvent } from 'react';

import { imageUrls } from '@shared/mappers';

import { useT } from '@/i18n/locale';
import { EASE_OUT, useRichMotion } from '@/lib/motion';
import { useMediaQuery } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';

/*
 * The auth marquee (ui-ux.md §11) — a projection-room panel of real,
 * recognisable covers: the product's promise ("your shows, covers-first")
 * shown before a word is read.
 *
 * Art: real posters of widely-known series, served from our own image CDN
 * (`poster_key`, mirrored by the image pipeline) with the TMDB CDN as the
 * fallback — both already allowed by the /web CSP `img-src`. (The App Store
 * screenshots use generated art for Apple's rules; the web has no such
 * constraint.) The list is static on purpose: /login is unauthenticated and
 * the API has no public catalog endpoint.
 *
 * Motion is two-tier (useRichMotion): a capable desktop gets the entrance
 * choreography, the slow column drift and pointer parallax; everything else
 * gets the same composition, still. Below lg the panel is not rendered at all
 * — not merely hidden — so phones never download the art.
 */

type Cover = { id: number; key: string; path: string };

// tmdb_id · poster_key · poster_path, from the prod catalog (GetItem SHOW#{id}).
const COVERS: Cover[] = [
  { id: 1396, key: 'images/posters/1396.jpg', path: '/anFx9aTOOYqgS3v7x3R84Kz67ly.jpg' }, // Breaking Bad
  { id: 66732, key: 'images/posters/show/66732.jpg', path: '/uOOtwVbSr4QDjAGIifLDwpb2Pdl.jpg' }, // Stranger Things
  { id: 100088, key: 'images/posters/100088.jpg', path: '/dmo6TYuuJgaYinXBPjrgG9mB5od.jpg' }, // The Last of Us
  { id: 95396, key: 'images/posters/95396.jpg', path: '/pPHpeI2X1qEd1CS1SeyrdhZ4qnT.jpg' }, // Severance
  { id: 1399, key: 'images/posters/show/1399/7d29b4a3c0e7b92f.jpg', path: '/1XS1oqL89opfnbLl8WnZY1O1uJx.jpg' }, // Game of Thrones
  { id: 136315, key: 'images/posters/136315.jpg', path: '/eKfVzzEazSIjJMrw9ADa2x8ksLz.jpg' }, // The Bear
  { id: 119051, key: 'images/posters/119051.jpg', path: '/9PFonBhy4cQy7Jz20NpMygczOkv.jpg' }, // Wednesday
  { id: 126308, key: 'images/posters/126308.jpg', path: '/7O4iVfOMQmdCSxhOg1WnzG1AgYT.jpg' }, // Shōgun
  { id: 76331, key: 'images/posters/76331.jpg', path: '/z0XiwdrCQ9yVIr4O0pxzaAYRxdW.jpg' }, // Succession
  { id: 94605, key: 'images/posters/show/94605.jpg', path: '/fqldf2t8ztc9aiwn3k6mlX3tvRT.jpg' }, // Arcane
  { id: 93405, key: 'images/posters/93405.jpg', path: '/1QdXdRYfktUSONkl1oD5gc6Be0s.jpg' }, // Squid Game
  { id: 60059, key: 'images/posters/show/60059/b416b3f55558800a.jpg', path: '/fC2HDm5t0kHl7mTm7jxMR31b7by.jpg' }, // Better Call Saul
  { id: 70523, key: 'images/posters/70523.jpg', path: '/apbrbWs8M9lyOpJYU5WXrpFbk1Z.jpg' }, // Dark
  { id: 94997, key: 'images/posters/94997.jpg', path: '/7V0Ebks0GgpKvQ7QbLAIdX5dos4.jpg' }, // House of the Dragon
  { id: 76479, key: 'images/posters/76479.jpg', path: '/in1R2dDc421JxsoRWaIIAqVI2KE.jpg' }, // The Boys
  { id: 111803, key: 'images/posters/111803.jpg', path: '/gbSaK9v1CbcYH1ISgbM7XObD2dW.jpg' }, // The White Lotus
];

// Four columns of four, alternating drift direction, each at its own pace so
// the wall never lines up into a visible loop.
const COLUMNS: Array<{ covers: Cover[]; direction: 'up' | 'down'; duration: string; offset: string }> = [
  { covers: COVERS.slice(0, 4), direction: 'up', duration: '110s', offset: '0%' },
  { covers: COVERS.slice(4, 8), direction: 'down', duration: '135s', offset: '-12%' },
  { covers: COVERS.slice(8, 12), direction: 'up', duration: '120s', offset: '-5%' },
  { covers: COVERS.slice(12, 16), direction: 'down', duration: '145s', offset: '-18%' },
];

/** Entrance choreography timings (rich tier only) — see ui-ux.md §11 storyboard. */
const ENTRANCE = {
  wall: { duration: 1.2, ease: EASE_OUT },
  columnStagger: 0.12,
  column: { duration: 1.0, ease: EASE_OUT },
  caption: { delay: 0.55, duration: 0.6, ease: EASE_OUT },
  body: { delay: 0.7, duration: 0.6, ease: EASE_OUT },
} as const;

/** The wall's turn, degrees. */
const WALL_ROTATE = -8;

/** Max parallax travel of the wall, px, at the panel's edge. */
const PARALLAX_PX = 14;

function CoverImage({ cover }: { cover: Cover }) {
  const { primary, fallback } = imageUrls(cover.key, cover.path, 'w342');
  const [src, setSrc] = useState(primary);
  if (!src) return <div className="aspect-[2/3] w-full rounded-sm bg-surface-alt" />;
  return (
    <img
      src={src}
      alt=""
      width={342}
      height={513}
      decoding="async"
      draggable={false}
      onError={() => setSrc((current) => (current !== fallback ? fallback : undefined))}
      className="aspect-[2/3] w-full select-none rounded-sm bg-marquee object-cover shadow-poster"
    />
  );
}

export function Marquee() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  if (!isDesktop) return null;
  return <MarqueePanel />;
}

function MarqueePanel() {
  const t = useT();
  const rich = useRichMotion();

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const x = useSpring(px, { stiffness: 60, damping: 20, mass: 0.6 });
  const y = useSpring(py, { stiffness: 60, damping: 20, mass: 0.6 });

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    if (!rich) return;
    const rect = event.currentTarget.getBoundingClientRect();
    // −1 … 1 from the panel centre; the wall moves AGAINST the pointer.
    const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    px.set(-nx * PARALLAX_PX);
    py.set(-ny * PARALLAX_PX);
  }

  function onPointerLeave() {
    px.set(0);
    py.set(0);
  }

  return (
    <aside
      aria-hidden="true"
      className="sticky top-0 h-dvh p-3"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <div className="relative isolate h-full overflow-hidden rounded-lg bg-marquee">
        {/* The wall — oversized and turned a few degrees so its edges never show. */}
        <motion.div
          className="absolute -inset-[18%] grid grid-cols-4 gap-4"
          // rotate lives in `style`, not a Tailwind class: framer-motion owns
          // this element's transform and would override a CSS rotate.
          style={rich ? { rotate: WALL_ROTATE, x, y } : { rotate: WALL_ROTATE }}
          initial={rich ? { opacity: 0, scale: 1.08 } : false}
          animate={rich ? { opacity: 1, scale: 1 } : undefined}
          transition={ENTRANCE.wall}
        >
          {COLUMNS.map((column, index) => (
            <motion.div
              key={index}
              className="relative"
              style={{ marginTop: column.offset }}
              initial={rich ? { opacity: 0, y: column.direction === 'up' ? 80 : -80 } : false}
              animate={rich ? { opacity: 1, y: 0 } : undefined}
              transition={{ ...ENTRANCE.column, delay: index * ENTRANCE.columnStagger }}
            >
              <div
                className={cn(
                  'flex flex-col gap-4',
                  rich && 'will-change-transform',
                  rich && (column.direction === 'up' ? 'animate-marquee-up' : 'animate-marquee-down'),
                )}
                style={{ '--marquee-dur': column.duration } as CSSProperties}
              >
                {[...column.covers, ...column.covers].map((cover, i) => (
                  <CoverImage key={`${cover.id}-${i}`} cover={cover} />
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Tint: knocks every cover down to a mid-tone so bright art (a white
            poster) can't compete with the caption. Then a scrim deepens the
            bottom where the caption sits, and a short one quiets the top edge. */}
        <div className="absolute inset-0 z-10 bg-marquee-tint" />
        <div className="absolute inset-x-0 bottom-0 z-10 h-[70%] bg-gradient-to-t from-marquee from-40% to-transparent" />
        <div className="absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-marquee to-transparent" />

        <div className="absolute inset-x-0 bottom-0 z-20 p-10 xl:p-14">
          <motion.p
            className="max-w-[16ch] font-display text-[2.75rem] font-bold leading-[1.02] tracking-[-0.035em] text-marquee-ink xl:text-[3.25rem]"
            initial={rich ? { opacity: 0, y: 16 } : false}
            animate={rich ? { opacity: 1, y: 0 } : undefined}
            transition={ENTRANCE.caption}
          >
            {t('auth.marquee.title')}
          </motion.p>
          <motion.p
            className="mt-5 max-w-[46ch] text-[1.0625rem] leading-relaxed text-marquee-muted"
            initial={rich ? { opacity: 0, y: 12 } : false}
            animate={rich ? { opacity: 1, y: 0 } : undefined}
            transition={ENTRANCE.body}
          >
            {t('auth.marquee.body')}
          </motion.p>
        </div>
      </div>
    </aside>
  );
}
